// Salesperson Detail Component
// Save as: ~/cfo-dashboard/frontend/src/components/SalespersonDetail.js

import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DollarSign, TrendingUp, Award } from 'lucide-react';
import JQBookings from './JQBookings';
import Boosters from './Boosters';
import SalesAnalytics from './SalesAnalytics';
import { BarChart3 } from 'lucide-react';
import './SalespersonDetail.css';

const API_BASE = '/api';

function SalespersonDetail({ month }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const queryMonth = searchParams.get('month') || month;
  const { token, isStaff } = useAuth();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestedTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(requestedTab || (isStaff ? 'analytics' : 'commission'));

  useEffect(() => {
    if (requestedTab === 'analytics' || requestedTab === 'commission') setActiveTab(requestedTab);
  }, [requestedTab, id]);

  useEffect(() => {
    fetchPersonData();
  }, [id, queryMonth]);

  const fetchPersonData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/salesperson/${id}?month=${queryMonth}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load salesperson data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!data) return null;

  const { person, deposits, jqBookings, boosters } = data;

  const formatCurrency = (amount) => {
    return `RM ${parseFloat(amount).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const isJQ = person.commission_type === 'rate_on_full_split';
  const isZenSplit = person.commission_type === 'zen_rate_on_full_split';

  return (
    <div className="salesperson-detail">
      <div className="detail-top-bar">
        <div className="tab-toggle">
          <button
            className={`tab-btn ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <BarChart3 size={15} /> Performance
          </button>
          <button
            className={`tab-btn ${activeTab === 'commission' ? 'active' : ''}`}
            onClick={() => setActiveTab('commission')}
          >
            Commission
          </button>
        </div>
      </div>

      <div className="person-header">
        <div className="person-info">
          <h2>{person.name}</h2>
          <p className="person-role">{person.role}</p>
        </div>
        <div className="person-status">
          {isZenSplit && person.current_tier_rate != null ? (
            Number(person.current_tier_rate) > 0
              ? <span className="badge success">✓ {Number(person.current_tier_rate).toFixed(2)}% Commission Tier</span>
              : <span className="badge warning">Below First Commission Tier</span>
          ) : isZenSplit ? (
            <span className="badge warning">Scheme Not Active</span>
          ) : person.meets_floor ? (
            <span className="badge success">✓ Meets Floor</span>
          ) : (
            <span className="badge warning">Below Floor</span>
          )}
        </div>
      </div>

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <SalesAnalytics salespersonId={person.id} />
      )}

      {/* Commission Tab */}
      {activeTab === 'commission' && (<>


      {/* Summary Cards - only for non-JQ salespeople */}
      {!isJQ && (
        <>
          <div className="summary-grid">
            <div className="summary-card">
              <div className="summary-icon revenue">
                <DollarSign size={24} />
              </div>
              <div className="summary-content">
                <p className="summary-label">Total Revenue</p>
                <h3>{formatCurrency(person.total_revenue || 0)}</h3>
              </div>
            </div>

            <div className="summary-card">
              <div className="summary-icon eligible">
                <TrendingUp size={24} />
              </div>
              <div className="summary-content">
                <p className="summary-label">Eligible Revenue</p>
                <h3>{formatCurrency(person.eligible_revenue || 0)}</h3>
                {person.excluded_deposit_amount > 0 && (
                  <p className="summary-note">
                    Excluded: {formatCurrency(person.excluded_deposit_amount)}
                  </p>
                )}
              </div>
            </div>

            <div className="summary-card">
              <div className="summary-icon commission">
                <Award size={24} />
              </div>
              <div className="summary-content">
                <p className="summary-label">Commission Earned</p>
                <h3>{formatCurrency(person.total_commission || 0)}</h3>
              </div>
            </div>

            <div className="summary-card highlight">
              <div className="summary-content">
                <p className="summary-label">Total Earnings</p>
                <h3>{formatCurrency(person.total_earned || 0)}</h3>
                <p className="summary-breakdown">
                  Basic: {formatCurrency(person.basic_salary)} +
                  Commission: {formatCurrency(person.total_commission || 0)}
                </p>
              </div>
            </div>
          </div>

          {/* Commission Breakdown */}
          <div className="breakdown-section">
            <h3>Commission Breakdown</h3>
            <div className="breakdown-table">
              {isZenSplit ? (
                <>
                  <div className="breakdown-row">
                    <span>70% Captured:</span>
                    <strong>{formatCurrency(person.zen_70_paid || 0)}</strong>
                  </div>
                  <div className="breakdown-row">
                    <span>Delivery Settlement:</span>
                    <strong>{formatCurrency(person.zen_30_settlements || 0)}</strong>
                  </div>
                </>
              ) : (
                <div className="breakdown-row">
                  <span>Tier Commission:</span>
                  <strong>{formatCurrency(person.tier_commission || 0)}</strong>
                </div>
              )}
              {person.name === 'Zac' && (
                <div className="breakdown-row">
                  <span>W Timber Booster ({parseFloat(person.w_timber_revenue || 0) < 50000 ? 'below RM 50k target' : 'hit target'}):</span>
                  <strong>{formatCurrency(person.booster_1_commission || 0)}</strong>
                </div>
              )}
              {person.name !== 'Zac' && person.booster_1_commission > 0 && (
                <div className="breakdown-row">
                  <span>Booster (Wedding/Birthday):</span>
                  <strong>{formatCurrency(person.booster_1_commission)}</strong>
                </div>
              )}
              {person.booster_2_commission > 0 && (
                <div className="breakdown-row">
                  <span>Booster (Event Space 1%):</span>
                  <strong>{formatCurrency(person.booster_2_commission)}</strong>
                </div>
              )}
              {person.hunting_commission > 0 && (
                <div className="breakdown-row">
                  <span>Hunting Commission:</span>
                  <strong>{formatCurrency(person.hunting_commission)}</strong>
                </div>
              )}
              <div className="breakdown-row total">
                <span>Total Commission:</span>
                <strong>{formatCurrency(person.total_commission || 0)}</strong>
              </div>
            </div>
          </div>

          {/* Venue/Brand Breakdown */}
          <div className="venue-section">
            <h3>Revenue by Venue / Brand</h3>
            <div className="venue-grid">
              {[
                { key: 'rmc_revenue', label: 'Runningman Catering' },
                { key: 'w_factory_revenue', label: 'W Factory' },
                { key: 'w_nexus_1utama_revenue', label: 'W Nexus (1 Utama)' },
                { key: 'w_timber_revenue', label: 'W Timber' },
                { key: 'w_sky_revenue', label: 'W in the Sky' },
                { key: 'w_nexus_kelana_revenue', label: 'W Nexus (Kelana Jaya)' },
                { key: 'w_nexus_kl_revenue', label: 'W Nexus (Kuala Lumpur)' },
                { key: 'outsource_marketing_revenue', label: 'Outsource Marketing' },
              ].filter(v => parseFloat(person[v.key] || 0) > 0).map(v => (
                <div className="venue-card" key={v.key}>
                  <p className="venue-name">{v.label}</p>
                  <p className="venue-amount">{formatCurrency(person[v.key])}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Deposit Exclusions */}
          {deposits && deposits.length > 0 && (
            <div className="deposits-section">
              <h3>Deposit Tracking</h3>
              <table className="deposits-table">
                <thead>
                  <tr>
                    <th>Seq</th>
                    <th>Invoice #</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {deposits.map((deposit) => (
                    <tr key={deposit.id} className={deposit.is_excluded ? 'excluded' : ''}>
                      <td>{deposit.sequence_number}</td>
                      <td>{deposit.invoice_number}</td>
                      <td>{new Date(deposit.collected_date).toLocaleDateString()}</td>
                      <td>{formatCurrency(deposit.deposit_amount)}</td>
                      <td>
                        {deposit.is_excluded ? (
                          <span className="badge warning">Excluded</span>
                        ) : (
                          <span className="badge success">Counted</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Boosters (Type 1: Wedding/Birthday, Type 2: Event Space) */}
          {(person.name === 'Ginny' || person.name === 'Zac') && (
            <div className="breakdown-section">
              <h3>Boosters</h3>
              <Boosters
                salespersonId={person.id}
                month={queryMonth}
                personName={person.name}
                excludeFirstN={person.exclude_first_n_deposits || 0}
                onBoosterChange={fetchPersonData}
              />
            </div>
          )}
        </>
      )}

      {/* JQ Booking Tracker - only for JQ */}
      {isJQ && (
        <JQBookings month={queryMonth} onBookingChange={fetchPersonData} />
      )}
      </>)}
    </div>
  );
}

export default SalespersonDetail;
